import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormSelect,
  CFormText,
  CFormTextarea,
  CRow,
  CSpinner,
} from "@coreui/react";

import { apiClient } from "services/Api";
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from "components/globals/AlertNotification";

const qualityOptions = [
  { value: 0, label: "Má" },
  { value: 1, label: "Fraca" },
  { value: 2, label: "Insuficiente" },
  { value: 3, label: "Boa" },
  { value: 4, label: "Muito Boa" },
  { value: 5, label: "Excelente" },
];

const recommendationOptions = [
  { value: 1, label: "Definitivamente Não" },
  { value: 2, label: "Muito Provavelmente Não" },
  { value: 3, label: "Provavelmente Não" },
  { value: 4, label: "Provavelmente Sim" },
  { value: 5, label: "Muito Provavelmente Sim" },
  { value: 6, label: "Definitivamente Sim" },
  { value: 0, label: "Não se Aplica" },
];

const PresentialSatisfactionSurvey = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState(null);
  const [clients, setClients] = useState([]);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const isViewOnly = new URLSearchParams(location.search).get("view") === "1";

  const numericFields = useMemo(
    () => new Set(["client", "service_quality", "future_project", "recommendation"]),
    []
  );

  const fetchSurveyDetails = async () => {
    try {
      const response = await apiClient.get(
        `/presential-satisfaction-surveys/${id}/`
      );
      setFormData(response.data);
    } catch (error) {
      triggerAlert("Questionário presencial não encontrado!", "error");
      navigate("/questionarios-satisfacao-presencial");
    }
  };

  useEffect(() => {
    apiClient
      .get("/clients/")
      .then((response) => {
        setClients(response.data || []);
      })
      .catch(() => {
        setToast({
          title: "Questionário Presencial",
          message: "Erro a carregar a lista de clientes",
          type: "error",
        });
      });
  }, []);

  useEffect(() => {
    if (id) {
      fetchSurveyDetails();
    } else {
      setFormData({
        client: null,
        service_quality: null,
        service_quality_comment: "",
        project_change: "",
        issue_occurred: null,
        issue_detail: "",
        issue_management: "",
        future_project: null,
        recommendation: null,
        suggestions: "",
      });
    }
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (numericFields.has(name)) {
      newValue = value === "" ? null : Number(value);
    } else if (name === "issue_occurred") {
      newValue = value === "" ? null : value === "true";
    }

    setFormData((prevFormData) => {
      const nextState = { ...prevFormData, [name]: newValue };
      if (name === "issue_occurred" && newValue === false) {
        nextState.issue_detail = "";
        nextState.issue_management = "";
      }
      return nextState;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        await apiClient.put(`/presential-satisfaction-surveys/${id}/`, formData);
        triggerAlert("Questionário atualizado com sucesso!", "success");
      } else {
        await apiClient.post("/presential-satisfaction-surveys/", formData);
        triggerAlert("Questionário criado com sucesso!", "success");
      }
      navigate("/questionarios-satisfacao-presencial");
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        "Erro ao gravar o questionário presencial";
      setToast({
        title: "Questionário Presencial",
        message: errorMessage,
        type: "error",
      });
      setLoading(false);
    }
  };

  if (!formData) {
    return (
      <div className="text-center">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (isViewOnly) {
    return (
      <div className="container mt-4">
        <CCard className="shadow-sm">
          <CCardHeader className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">Questionário Presencial</h4>
              <div className="text-medium-emphasis small">
                #{formData.id || "-"} • {formData.created_at || "-"}
              </div>
            </div>
            <div className="d-flex gap-2">
              <CButton
                color="info"
                className="text-white"
                onClick={() =>
                  navigate(`/questionarios-satisfacao-presencial/${id}`)
                }
              >
                Editar
              </CButton>
              <CButton
                color="secondary"
                variant="outline"
                onClick={() => navigate("/questionarios-satisfacao-presencial")}
              >
                Voltar
              </CButton>
            </div>
          </CCardHeader>
          <CCardBody className="d-flex flex-column gap-4">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <div className="text-medium-emphasis small">
                {formData.created_by_name || "-"}
              </div>
            </div>

            <CRow className="g-3">
              <CCol md={6}>
                <div className="border rounded-3 p-3 bg-body-tertiary h-100">
                  <div className="text-uppercase text-medium-emphasis small">
                    Cliente
                  </div>
                  <div className="fw-semibold">
                    {formData.client_name || "-"}
                  </div>
                </div>
              </CCol>
              <CCol md={6}>
                <div className="border rounded-3 p-3 bg-body-tertiary h-100">
                  <div className="text-uppercase text-medium-emphasis small">
                    Qualidade do serviço
                  </div>
                  <div className="fw-semibold">
                    {qualityOptions.find(
                      (option) => option.value === formData.service_quality
                    )?.label || "-"}
                  </div>
                </div>
              </CCol>
            </CRow>

            <div className="border rounded-3 p-3 bg-body-tertiary">
              <div className="text-uppercase text-medium-emphasis small">
                Comentários sobre a qualidade do serviço
              </div>
              <div className="fw-semibold">
                {formData.service_quality_comment || "-"}
              </div>
            </div>

            <div className="border rounded-3 p-3 bg-body-tertiary">
              <div className="text-uppercase text-medium-emphasis small">
                O que faria de diferente?
              </div>
              <div className="fw-semibold">
                {formData.project_change || "-"}
              </div>
            </div>

            <div className="border rounded-3 p-3 bg-body-tertiary">
              <div className="text-uppercase text-medium-emphasis small">
                Houve algum aspeto que correu mal?
              </div>
              <div className="fw-semibold">
                {formData.issue_occurred === true
                  ? "Sim"
                  : formData.issue_occurred === false
                  ? "Não"
                  : "-"}
              </div>
            </div>

            {formData.issue_occurred ? (
              <>
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Qual
                  </div>
                  <div className="fw-semibold">
                    {formData.issue_detail || "-"}
                  </div>
                </div>
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Gestão do problema
                  </div>
                  <div className="fw-semibold">
                    {formData.issue_management || "-"}
                  </div>
                </div>
              </>
            ) : null}

            <div className="border rounded-3 p-3 bg-body-tertiary">
              <div className="text-uppercase text-medium-emphasis small">
                Próximo projeto
              </div>
              <div className="fw-semibold">
                {recommendationOptions.find(
                  (option) => option.value === formData.future_project
                )?.label || "-"}
              </div>
            </div>

            <div className="border rounded-3 p-3 bg-body-tertiary">
              <div className="text-uppercase text-medium-emphasis small">
                Recomendação
              </div>
              <div className="fw-semibold">
                {recommendationOptions.find(
                  (option) => option.value === formData.recommendation
                )?.label || "-"}
              </div>
            </div>

            <div className="border rounded-3 p-3 bg-body-tertiary">
              <div className="text-uppercase text-medium-emphasis small">
                Sugestões
              </div>
              <div className="fw-semibold">
                {formData.suggestions || "-"}
              </div>
            </div>
          </CCardBody>
        </CCard>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <ToastNotification toast={toast} setToast={setToast} />

      <CCard className="shadow-sm">
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-1">
              {id ? "Editar Questionário Presencial" : "Novo Questionário"}
            </h4>
            <div className="text-medium-emphasis small">
              Registe a opinião do cliente presencialmente.
            </div>
          </div>
          <div className="d-flex gap-2">
            <CButton
              color="secondary"
              variant="outline"
              onClick={() => navigate("/questionarios-satisfacao-presencial")}
              disabled={loading}
            >
              Cancelar
            </CButton>
            <CButton
              color="info"
              className="text-white"
              type="submit"
              form="presential-survey-form"
              disabled={loading}
            >
              {loading ? "A guardar..." : "Guardar"}
            </CButton>
          </div>
        </CCardHeader>
        <CCardBody>
          <CForm id="presential-survey-form" onSubmit={handleSubmit}>
            <CFormText className="text-muted mb-4">
              Campos obrigatórios assinalados com *
            </CFormText>

            <div className="border rounded-3 p-3 bg-body-tertiary mb-4">
              <div className="text-uppercase text-medium-emphasis small mb-3">
                Identificação
              </div>
              <CRow className="g-3">
                <CCol md={12}>
                  <CFormSelect
                    label="Cliente *"
                    name="client"
                    value={formData.client ?? ""}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Selecione...</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
              </CRow>
            </div>

            <div className="border rounded-3 p-3 bg-body-tertiary mb-4">
              <div className="text-uppercase text-medium-emphasis small mb-3">
                Questionário
              </div>
              <CRow className="g-3">
                <CCol md={12}>
                  <CFormSelect
                    label="Globalmente, como classifica a qualidade dos serviços prestados pela STREAM? *"
                    name="service_quality"
                    value={formData.service_quality ?? ""}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Selecione...</option>
                    {qualityOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
                <CCol md={12}>
                  <CFormTextarea
                    label="Comentários sobre a qualidade do serviço"
                    name="service_quality_comment"
                    value={formData.service_quality_comment || ""}
                    onChange={handleInputChange}
                    rows={3}
                  />
                </CCol>
                <CCol md={12}>
                  <CFormTextarea
                    label="Se fosse realizar o projeto agora, o que faria de diferente?"
                    name="project_change"
                    value={formData.project_change || ""}
                    onChange={handleInputChange}
                    rows={3}
                  />
                </CCol>
                <CCol md={12}>
                  <CFormSelect
                    label="Houve algum aspeto que correu mal? *"
                    name="issue_occurred"
                    value={
                      formData.issue_occurred === null ||
                      formData.issue_occurred === undefined
                        ? ""
                        : formData.issue_occurred
                        ? "true"
                        : "false"
                    }
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Selecione...</option>
                    <option value="true">Sim</option>
                    <option value="false">Não</option>
                  </CFormSelect>
                </CCol>
                {formData.issue_occurred ? (
                  <>
                    <CCol md={12}>
                      <CFormTextarea
                        label="Qual"
                        name="issue_detail"
                        value={formData.issue_detail || ""}
                        onChange={handleInputChange}
                        rows={3}
                      />
                    </CCol>
                    <CCol md={12}>
                      <CFormTextarea
                        label="Como avalia a forma como a STREAM geriu esse problema?"
                        name="issue_management"
                        value={formData.issue_management || ""}
                        onChange={handleInputChange}
                        rows={3}
                      />
                    </CCol>
                  </>
                ) : null}
                <CCol md={12}>
                  <CFormSelect
                    label="Da próxima vez que pretender realizar um projeto, irá entregar esse projeto à STREAM? *"
                    name="future_project"
                    value={formData.future_project ?? ""}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Selecione...</option>
                    {recommendationOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
                <CCol md={12}>
                  <CFormSelect
                    label="Por último, recomendaria os serviços da STREAM a outras empresas? *"
                    name="recommendation"
                    value={formData.recommendation ?? ""}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Selecione...</option>
                    {recommendationOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
                <CCol md={12}>
                  <CFormTextarea
                    label="Sugestões"
                    name="suggestions"
                    value={formData.suggestions || ""}
                    onChange={handleInputChange}
                    rows={3}
                  />
                </CCol>
              </CRow>
            </div>
          </CForm>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default PresentialSatisfactionSurvey;
